import { Trans } from '@lingui/react'
import { useAtom } from 'jotai'

import { useAcceptPlBasicAccountAgreementsMutation } from '@twisto/api-graphql'
import { FormSubmitButton } from '@twisto/components/atoms/button'
import { Flex } from '@twisto/components/atoms/flex'
import { useForm } from '@twisto/components/atoms/form'
import type { CastedSubmitHandler } from '@twisto/components/atoms/form'
import { Link } from '@twisto/components/atoms/link'
import { Checkbox, CheckboxGroup } from '@twisto/components/molecules/checkbox'
import { useResponseErrors } from '@twisto/form'
import { useOpen } from '@twisto/hooks'

import { FlowForm, PepCheckbox, submittingFlowFormAtom } from '../../components'
import { useFlow } from '../../flow/flow.context'

import { PlBasicAccountAgreementsStepHeader } from './pl-basic-account-agreements-step-header'
import * as styles from './pl-basic-account-agreements-step.styles'

const checkboxGroupFieldName = 'allConsents'

const fieldNames = {
  marketing: 'marketing',
  isPoliticallyExposed: 'isPoliticallyExposed',
  contacting: 'contacting',
  termsPrivacy: 'termsPrivacy',
  crif: 'crif',
  bik: 'bik',
  creditBureau: 'creditBureau',
} as const

type FormValues = {
  [fieldNames.marketing]: boolean
  [fieldNames.isPoliticallyExposed]: boolean
  [fieldNames.contacting]: boolean
  [fieldNames.termsPrivacy]: boolean
  [fieldNames.crif]: boolean
  [fieldNames.bik]: boolean
  [fieldNames.creditBureau]: boolean
  [checkboxGroupFieldName]?: boolean
}

export const PlBasicAccountAgreementsStep = () => {
  const { isOpen, handleClose, handleOpen } = useOpen()
  const { flow, sessionId } = useFlow<'PlBasicAccountAgreementsStep'>()
  const [submitting] = useAtom(submittingFlowFormAtom)
  const {
    bik,
    contacting,
    creditBureau,
    crif,
    isPoliticallyExposed,
    marketing,
    termsPrivacy,
  } = flow.currentStep

  const formContext = useForm({
    defaultValues: {
      [fieldNames.marketing]: marketing.value ?? false,
      [fieldNames.isPoliticallyExposed]: isPoliticallyExposed.value ?? false,
      [fieldNames.contacting]: contacting.value ?? false,
      [fieldNames.termsPrivacy]: termsPrivacy.value ?? false,
      [fieldNames.crif]: crif.value ?? false,
      [fieldNames.bik]: bik.value ?? false,
      [fieldNames.creditBureau]: creditBureau.value ?? false,
    },
  })

  const [acceptTwistoPlBasicAccountConsents, { data }] =
    useAcceptPlBasicAccountAgreementsMutation()

  // handle field errors - such as invalid email
  useResponseErrors(data?.acceptPlBasicAccountAgreements, formContext)

  const handleSubmit: CastedSubmitHandler<FormValues> = async (
    values: FormValues
  ) => {
    const submittedFormValues: FormValues = {
      ...values,
    }
    // detele checkbox group field from submitted values (check all button)
    delete submittedFormValues[checkboxGroupFieldName]

    acceptTwistoPlBasicAccountConsents({
      variables: { input: submittedFormValues, auth: { sessionId } },
    })
  }

  const consents = [
    {
      label: (
        <Trans
          components={[
            <Link
              key="accountTerms"
              href={termsPrivacy.termsService.url}
              target="_blank"
            >
              {null}
            </Link>,
            <Link
              key="accountPrecontractualForm"
              href={termsPrivacy.termsTariffs.url}
              target="_blank"
            >
              {null}
            </Link>,
            <Link
              key="privacyPolicy"
              href={termsPrivacy.privacyPolicy.url}
              target="_blank"
            >
              {null}
            </Link>,
            <Link
              key="referralProgramme"
              href={termsPrivacy.referralProgramme.url}
              target="_blank"
            >
              {null}
            </Link>,
            <Link
              key="trialProgram"
              href={termsPrivacy.trialProgram.url}
              target="_blank"
            >
              {null}
            </Link>,
            <Link
              key="cardTerms"
              href={termsPrivacy.cardTerms.url}
              target="_blank"
            >
              {null}
            </Link>,
            <Link
              key="paramInformationalClause"
              href={termsPrivacy.paramInformationalClause.url}
              target="_blank"
            >
              {null}
            </Link>,
          ]}
          id="flow.plBasicAccountAgreements.privacyTerms"
        />
      ),
      name: fieldNames.termsPrivacy,
      required: termsPrivacy.required,
    },
    {
      label: (
        <Trans
          components={[
            <Link key="crif" href={crif.document.url} target="_blank">
              {null}
            </Link>,
          ]}
          id="flow.plBasicAccountAgreements.crif"
        />
      ),
      name: fieldNames.crif,
      required: crif.required,
    },
    {
      label: (
        <Trans
          components={[
            <Link key="bik" href={bik.document.url} target="_blank">
              {null}
            </Link>,
          ]}
          id="flow.plBasicAccountAgreements.bik"
        />
      ),
      name: fieldNames.bik,
      required: bik.required,
    },
    {
      label: (
        <Trans
          components={[
            <Link
              key="creditBureau"
              href={creditBureau.document.url}
              target="_blank"
            >
              {null}
            </Link>,
          ]}
          id="flow.plBasicAccountAgreements.creditBureau"
        />
      ),
      name: fieldNames.creditBureau,
      required: creditBureau.required,
    },
    {
      label: (
        <Trans
          components={[
            <Link
              key="contacting"
              href={contacting.document.url}
              target="_blank"
            >
              {null}
            </Link>,
          ]}
          id="flow.plBasicAccountAgreements.contacting"
        />
      ),
      name: fieldNames.contacting,
      required: contacting.required,
    },
    {
      label: (
        <Trans
          components={[
            <Link key="marketing" href={marketing.document.url} target="_blank">
              {null}
            </Link>,
          ]}
          id="flow.plBasicAccountAgreements.marketing"
        />
      ),
      name: fieldNames.marketing,
      required: marketing.required,
    },
  ]

  return (
    <FlowForm
      {...formContext}
      onSubmit={
        formContext.getValues(fieldNames.isPoliticallyExposed)
          ? handleOpen
          : handleSubmit
      }
    >
      <PlBasicAccountAgreementsStepHeader />
      <CheckboxGroup
        autoFocus
        collapseDisabled
        collapsed={false}
        ignoredFields={[fieldNames.isPoliticallyExposed]}
        name={checkboxGroupFieldName}
        toggleAllCheckboxLabel={
          <Trans id="flow.plBasicAccountAgreementsStep..selectAll" />
        }
      >
        {consents.map(({ label, name, required }) => (
          <Flex key={name} alignItems="center" css={styles.checkboxWrapper}>
            <Checkbox
              css={styles.checkbox}
              id={name}
              label={label}
              name={name}
              required={required}
            />
          </Flex>
        ))}

        <PepCheckbox
          confirmationModalOpened={isOpen}
          description={
            <Trans id="flow.plBasicAccountAgreements.isPoliticallyExposed" />
          }
          hasWarning={formContext.getValues('isPoliticallyExposed')}
          name={fieldNames.isPoliticallyExposed}
          onClose={handleClose}
          onConfirm={formContext.handleSubmit(handleSubmit)}
        />
      </CheckboxGroup>

      <FormSubmitButton disabled={submitting}>
        <Trans id="flow.plBasicAccountAgreementsStep.submitButton" />
      </FormSubmitButton>
    </FlowForm>
  )
}
